import {
    ChangeDetectionStrategy, Component, Input, OnInit, ViewChild, ElementRef, HostListener, HostBinding, ChangeDetectorRef,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, switchMap } from 'rxjs';
import { distinctUntilChanged, filter, map, take, tap } from 'rxjs/operators';

import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { ApplicationSections } from '@core/domain/models/application-section.model';
import { getLanguageisRTL } from '@core/infrastructure/state/core.selectors';
import { ContentItemTree } from '@features/cms-render/domain/models/contentItemTree';
import { Link } from '@features/cms-render/domain/models/link';
import {
    CommonStylesRenderDirective,
} from '@features/cms-render/ui/directives/common-styles-render/common-styles-render.directive';
import { MenuCategoriesService } from '@shared/menu-categories/services/menu-categories.service';
import { getLastSectionSelector } from '@shared/menu-categories/store/menu-categories.selectors';
import { NavigationService } from '@shared/services/navigation/navigation.service';

@Component({
    selector: 'piece-seo-categories',
    templateUrl: './piece-seo-categories.component.html',
    styleUrls: ['./piece-seo-categories.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MenuCategoriesService],
})

/**
 * Componente para mostrar piezas de tipo "Seo Categories"
 * TODO: rehacer este componente cuando esté la pieza nativa de CMS hecha
 */
export class PieceSeoCategoriesComponent extends CommonStylesRenderDirective implements OnInit {
    @Input() public element: ContentItemTree;

    public categories$: Observable<Link[]>;

    private isDown = false;

    private startX: number;

    public scrollLeft: number;

    public isRtl$: Observable<boolean> = this.store.select(getLanguageisRTL);

    @ViewChild('divContainer') public divContainer: ElementRef;

    public textLeft: string;

    public excludeCategories: string[];

    @HostBinding('style.--background-color')
    public backgroundColor: string | undefined;

    @HostBinding('style.--text-color')
    public textColor: string | undefined;

    @BrowserOnly<PieceSeoCategoriesComponent, undefined>(undefined)
    @HostListener('mousedown', ['$event'])
    public scrolling(e: MouseEvent): void {
        this.isDown = true;
        this.startX = e.pageX - this.divContainer.nativeElement.offsetLeft;
        this.scrollLeft = this.divContainer.nativeElement.scrollLeft;
    }

    @BrowserOnly<PieceSeoCategoriesComponent, undefined>(undefined)
    @HostListener('mouseleave', ['$event'])
    @HostListener('mouseup', ['$event'])
    public scrollingStop(): void {
        this.isDown = false;
    }

    @BrowserOnly<PieceSeoCategoriesComponent, undefined>(undefined)
    @HostListener('mousemove', ['$event'])
    public mouseUp(e: MouseEvent): void {
        if (!this.isDown) return;
        e.preventDefault();
        const x = e.pageX - this.divContainer.nativeElement.offsetLeft;
        const walk = (x - this.startX) * 3; // scroll-fast
        this.divContainer.nativeElement.scrollLeft = this.scrollLeft - walk;
    }

    public isLightTextColor: boolean = true;

    public opacityGrandientStart = 0;

    public opacityGrandientEnd = 0;

    public opacityArrowStart: number = 0;

    public opacityArrowEnd: number = 0;

    public categoryId: number;

    constructor(
        private readonly menuCategoriesService: MenuCategoriesService,
        private readonly navigationService: NavigationService,
        private readonly store: Store,
        private readonly elRef: ElementRef,
        private readonly cdr: ChangeDetectorRef,
    ) {
        super();
    }

    public ngOnInit(): void {
        super.setDefaultHostStyles();

        this.backgroundColor = this.element.settings.general.backgroundColor ?? '#292929';
        this.textColor = this.element.settings.general.textColor ?? '#FFFFFF';
        this.isLightTextColor = this.hexIsLight(this.textColor);
        this.categoryId = this.element.settings.general.categoryId;

        this.textLeft = this.element.settings.general.textLeft;

        this.excludeCategories = this.element.settings.general.excludeCategories ?? [];
        this.categories$ = this.store.select(getLastSectionSelector).pipe(
            filter(lastSection => !!lastSection),
            distinctUntilChanged(),
            map(section => {
                const isWomen = section === ApplicationSections.WOMEN;
                const isMen = section === ApplicationSections.MEN;
                const isBg = section === ApplicationSections.BG;
                if (this.element.settings.general.categoryWomenId && isWomen) {
                    this.categoryId = this.element.settings.general.categoryWomenId;
                }
                if (this.element.settings.general.categoryMenId && isMen) {
                    this.categoryId = this.element.settings.general.categoryMenId;
                }
                if (this.element.settings.general.categoryBgId && isBg) {
                    this.categoryId = this.element.settings.general.categoryBgId;
                }
                return this.categoryId;
            }),
            filter(categoryId => !!categoryId),
            switchMap(categoryId=> {
                return this.menuCategoriesService.getCategory(categoryId.toString()).pipe(
                    map((categories) => {
                        return categories.filter(item =>
                            !this.excludeCategories
                                .map(excludeId => excludeId.toString())
                                .includes(item.categoryId.toString()))
                            .map((x: Link) => {
                                return { ...x, pieceName: 'piece-seo-categories' };
                            });
                    }),
                );
            }),
            tap(() => {
                this.isRtl$.pipe(take(1)).subscribe((isRtl) => {
                    this.setOpactityArrowEndInit(isRtl);
                });
            })
        );
    }

    @BrowserOnly<PieceSeoCategoriesComponent, Promise<boolean>>(Promise.resolve(false))
    public async setOpactityArrowEndInit(isRtl) {
        const listElement: any = await this.elRef.nativeElement;
        setTimeout(() => {
            const { offsetWidth, scrollWidth } = listElement.querySelector('.footer-goto-list');
            const opacity = (offsetWidth ?? 0) > (scrollWidth ?? 0) ? 0 : 1;
            if (!isRtl) {
                this.opacityArrowEnd = opacity;
            } else {
                this.opacityArrowStart = opacity;
            }
            this.cdr.detectChanges();
        }, 1000);
    }

    /**
     * Función para navegar a una categoría
     */
    public goToCategory(event: any, path: string): void {
        event.preventDefault();
        event.stopPropagation();

        this.navigationService.redirect(path);
    }

    public trackByIndex(index: number): number {
        return index;
    }

    public scrollBy(scrollTo: number) {
        this.divContainer.nativeElement.scrollBy({
            top: 0,
            left: scrollTo,
            behavior: 'smooth',
        });
    }

    public scrollStart() {
        this.scrollBy(-200);
    }

    public scrollEnd() {
        this.scrollBy(200);
    }

    public onScroll($event: Event, isRtl: boolean) {
        const target = $event.target;
        const { scrollLeft, scrollWidth, clientWidth } = target as HTMLElement;

        const scrollLeftAbs = Math.abs(scrollLeft);

        let opacityStart: number;
        let opacityEnd: number;

        // Para opacityStart
        if (scrollLeftAbs <= 32) {
            opacityStart = scrollLeftAbs / 32;
        } else {
            opacityStart = 1;
        }

        // Para opacityEnd
        const distanceToEnd = scrollWidth - (scrollLeftAbs + clientWidth);
        if (distanceToEnd <= 32) {
            opacityEnd = distanceToEnd / 32;
        } else {
            opacityEnd = 1;
        }

        this.opacityGrandientStart = opacityStart;
        this.opacityGrandientEnd = opacityEnd;

        if (!isRtl) {
            this.opacityArrowStart = opacityStart > 0.4 ? 1 : 0;
            this.opacityArrowEnd = opacityEnd > 0.4 ? 1 : 0;
        } else {
            this.opacityArrowEnd = opacityStart > 0.4 ? 1 : 0;
            this.opacityArrowStart = opacityEnd > 0.4 ? 1 : 0;
        }

        this.cdr.detectChanges();
    }

    private hexIsLight(color) {
        const hex = color.replace('#', '');
        const cR = parseInt(hex.substr(0, 2), 16);
        const cG = parseInt(hex.substr(2, 2), 16);
        const cB = parseInt(hex.substr(4, 2), 16);
        const brightness = (cR * 299 + cG * 587 + cB * 114) / 1000;

        return brightness > 155;
    }
}
